import {
  LABELS,
  DEFAULT_PRODUCT_SLIDER,
  transformBannersToBannerBlock,
} from '../lib'
import { useHomepage } from '../state'
import { BannerSlider } from './BannerSlider'
import { BrandCardsGrid } from './BrandCards'
import { CategoryCardsGrid } from './CategoryCards'
import { HomepageSlot } from './HomepageSlot'
import { ProductSlider } from './ProductSlider'
import { PromoBlockRow } from './PromoBlock'
import { ReviewsCarousel } from './ReviewsCarousel'
import { SeoText } from './SeoText'
import { TrustBar } from './TrustBar'

import type { HomepagePromoBlock } from '../types'

/** Split promo blocks into rows of two (last row may hold a single block). */
function chunkPairs(blocks: HomepagePromoBlock[]): HomepagePromoBlock[][] {
  const rows: HomepagePromoBlock[][] = []
  for (let i = 0; i < blocks.length; i += 2) {
    rows.push(blocks.slice(i, i + 2))
  }
  return rows
}

function HomepageLoadError({ error }: { error: Error }) {
  return (
    <div className="text-center py-12">
      <p className="text-custom-red">{LABELS.HOMEPAGE_LOADING_ERROR}</p>
      <p className="text-second-btn-bg text-sm mt-2">
        {error instanceof Error ? error.message : String(error)}
      </p>
    </div>
  )
}

/**
 * HomepageSections — fixed-slot layout (TSS-535 contract)
 *
 * The top homepage slots are positional, NOT sort_order-driven. Promo blocks
 * render TWO-PER-ROW (PromoBlockRow):
 *   slot 1: BannerSlider
 *   slot 2: TrustBar
 *   slot 3: CategoryCardsGrid (if any)
 *   slot 4: PromoBlock[0..1]  (first pair)
 *   slot 5: BrandCardsGrid (if any)
 *   slot 6: PromoBlock[2..3]  (second pair)
 *   slot 7: ProductSlider[0]
 *   slot 8+: PromoBlock[4..N] in pairs, then ProductSlider[1..N]
 *
 * If editors need a different ordering (e.g. drag-reorder via sort_order),
 * this component must be refactored to consume a single sections[] array
 * sorted by sort_order. See review finding M-1 (slug
 * 20260501-1040-toolsstore-feature-tss-535-homepage-react-…).
 */
export function HomepageSections() {
  const {
    banners,
    productSliders,
    promoBlocks,
    categoryCards,
    brandCards,
    isLoading,
    error,
    reviews,
    reviewsLoading,
  } = useHomepage()

  if (error) {
    return <HomepageLoadError error={error} />
  }

  return (
    <div className="homepage-sections bg-cards-bg">
      <HomepageSlot name="BannerSlider" errorMessage={LABELS.BANNER_SLIDER_ERROR} kind="banner">
        <BannerSlider banners={transformBannersToBannerBlock(banners)} isLoading={isLoading} />
      </HomepageSlot>

      {/* Fixed hardcoded slot (TSS-535 contract): trust / USP bar sits directly
          under the hero banner. Static content from TrustBar/trustItems —
          no admin, no data dependency. */}
      <HomepageSlot name="TrustBar" errorMessage={LABELS.TRUST_BAR_ERROR}>
        <TrustBar />
      </HomepageSlot>

      {categoryCards.length > 0 && (
        <HomepageSlot name="CategoryCards" errorMessage={LABELS.CATEGORY_CARDS_ERROR}>
          <CategoryCardsGrid cards={categoryCards} isLoading={isLoading} />
        </HomepageSlot>
      )}

      {(promoBlocks.length > 0 || isLoading) && (
        <HomepageSlot name="PromoBlock-Row-1" errorMessage={LABELS.PROMO_BLOCK_ERROR}>
          <PromoBlockRow blocks={promoBlocks.slice(0, 2)} isLoading={isLoading} />
        </HomepageSlot>
      )}

      {brandCards.length > 0 && (
        <HomepageSlot name="BrandCards" errorMessage={LABELS.BRAND_CARDS_ERROR}>
          <BrandCardsGrid cards={brandCards} isLoading={isLoading} />
        </HomepageSlot>
      )}

      {promoBlocks.length > 2 && (
        <HomepageSlot name="PromoBlock-Row-2" errorMessage={LABELS.PROMO_BLOCK_ERROR}>
          <PromoBlockRow blocks={promoBlocks.slice(2, 4)} />
        </HomepageSlot>
      )}

      {(productSliders[0] || isLoading) && (
        <HomepageSlot name="ProductSlider-1" errorMessage={LABELS.PRODUCT_SLIDER_ERROR}>
          <ProductSlider
            key={productSliders[0]?.id ?? 'slider-skeleton-1'}
            slider={productSliders[0] ?? DEFAULT_PRODUCT_SLIDER}
            isLoading={isLoading}
          />
        </HomepageSlot>
      )}

      {chunkPairs(promoBlocks.slice(4)).map((pair) => (
        <HomepageSlot
          key={`promo-row-${pair[0].id}`}
          name={`PromoBlock-Row-${pair[0].id}`}
          errorMessage={LABELS.PROMO_BLOCK_ERROR}
        >
          <PromoBlockRow blocks={pair} />
        </HomepageSlot>
      ))}

      {productSliders.slice(1).map((slider) => (
        <HomepageSlot
          key={slider.id}
          name={`ProductSlider-${slider.id}`}
          errorMessage={LABELS.PRODUCT_SLIDER_ERROR}
        >
          <ProductSlider slider={slider} />
        </HomepageSlot>
      ))}

      {/* Fixed hardcoded slot (TSS-535 contract): reviews carousel sits after the
          product sliders, near the page bottom. Move this block to reposition.
          Reviews come from a separate query; shown whenever there are reviews.
          A query that fails with no cached reviews leaves the list empty, so the
          section is hidden the same way; useHomepageReviews logs the failure. */}
      {(reviewsLoading || reviews.length > 0) && (
        <HomepageSlot name="ReviewsCarousel" errorMessage={LABELS.REVIEWS_CAROUSEL_ERROR}>
          <ReviewsCarousel reviews={reviews} isLoading={reviewsLoading} />
        </HomepageSlot>
      )}

      {/* Fixed hardcoded slot (TSS-535 contract): SEO editorial copy sits at the
          very bottom of the homepage, after every other section. Static text,
          no admin, no data dependency. */}
      <HomepageSlot name="SeoText" errorMessage={LABELS.SEO_TEXT_ERROR}>
        <SeoText />
      </HomepageSlot>
    </div>
  )
}
